<template>
  <div>
    <SlideBlur v-if="!contentIsLoaded" />

    <div class="frisbuy-story-slide__wrapper">
      <div
        class="frisbuy-story-slide"
        :class="{ 'frisbuy-blur-background': !contentIsLoaded }"
        @click.self.stop="resumeStorie"
        @mousedown.stop
        :style="{
          ...storyImagePositionStyles,
          ...backgroundImageStyles,
        }"
      >
        <img
          :data-src="imageSource.imageHighRes"
          ref="image"
          @error="errorImageHandler"
        />
        <video
          v-if="storyMedia.videoUrl"
          :muted="!preview.playWithSound"
          ref="video"
          playsinline
          preload="metadata"
          @error="errorVideoHandler"
          :data-src="storyMedia.videoUrl"
          @click.self.stop="resumeStorie"
          @mousedown.stop
        ></video>
      </div>
    </div>

    <InteractionWrapper ref="InteractionWrapper">
      <SlideNavigation
        v-if="isShowNavigation"
        :isShowPrev="isShowNavigationPrev"
        :isShowNext="isShowNavigationNext"
        navigationType="arrows"
        @clickPrev="$emit('prevSlide')"
        @clickNext="$emit('nextSlide')"
        @touchstart="$emit('navigationTouchPause')"
      />

      <EmojisAnimation
        v-if="interactions"
        :isShowEmojiAnimation="isShowEmojiAnimation"
        :emojiIconUrl="clickedEmojiIconUrl"
      />
      <div
        v-for="(interaction, index) in interactions"
        v-show="contentIsLoaded"
        :style="getInteractionPositionStyles(interaction)"
        :key="`${interaction.type}_${index}`"
        class="frisbuy-interaction-frame"
      >
        <EmojisKit
          v-if="interaction.type === 'emoji'"
          :interaction="interaction"
          @clickEmoji="
            (emoji) =>
              onClickInteractionHandler({
                type: interaction.type,
                index: index,
                ...emoji,
              })
          "
        />
        <PollForm
          v-else-if="interaction.type === 'poll'"
          :interaction="interaction"
          :isQuerySuccess="isPollQuerySuccess"
          @chooseAnswer="
            (answer) =>
              onClickInteractionHandler({
                index: index,
                type: interaction.type,
                answer: answer,
              })
          "
        />
        <QuestionForm
          v-else-if="interaction.type === 'question'"
          :interaction="interaction"
          :isQuerySuccess="isQuestionQuerySuccess"
          @selectInput="() => questionFormSelectInputHandler(true)"
          @unSelectInput="() => questionFormSelectInputHandler(false)"
          @getAnswer="
            (answer) =>
              onClickInteractionHandler({ type: interaction.type, ...answer })
          "
        />
      </div>

      <template v-if="stickers?.pictureStickers">
        <PictureSticker
          v-for="(pictureSticker, index) in stickers.pictureStickers"
          v-show="contentIsLoaded"
          :key="`pictureSticker_${index}`"
          :sticker="pictureSticker"
          :style="getInteractionPositionStyles(pictureSticker)"
        />
      </template>

      <template v-if="stickers?.textStickers">
        <TextSticker
          v-for="(textSticker, index) in stickers.textStickers"
          v-show="contentIsLoaded"
          :key="`textSticker_${index}`"
          :sticker="textSticker"
          :style="{
            ...getInteractionPositionStyles(textSticker),
          }"
        />
      </template>

      <template v-if="callToActions">
        <template
          v-for="(action, index) in callToActions"
          :key="`callToAction_${index}`"
        >
          <CallToAction
            v-show="contentIsLoaded"
            @click.prevent="() => $emit('pauseSlide')"
            @clickLink="
              (link, linkTitle) => sendClickLinkAnalytic(link, linkTitle)
            "
            :callToAction="action"
            :style="{
              ...getInteractionPositionStyles(action),
            }"
          ></CallToAction>
        </template>
      </template>
    </InteractionWrapper>
  </div>
</template>

<script>
import { recoveryURL } from "@/api/api";
import analytics from "@/common/mixins/analytics";
import { SlideBlur } from "@/components/stories";
import { getImagePositionStyles } from "@/scripts/services";
import {
  EmojisKit,
  PollForm,
  QuestionForm,
  InteractionWrapper,
  EmojisAnimation,
  CallToAction,
} from "@/components/interactions";
import { getAnonimousUserCode, sendInteraction } from "@/api/interactions";
import SlideNavigation from "@/components/stories/SlideNavigation.vue";
import { PictureSticker, TextSticker } from "@/components/stickers";
import { errorImageHandler, errorVideoHandler } from "@/api/errorHandlers";

export default {
  // TODO: rewrite mixins to pure js methods or use composition api
  // for importing like sendAnalyticEvent from "@/common/mixins/analytics";
  mixins: [analytics],

  components: {
    SlideBlur,
    EmojisKit,
    PollForm,
    QuestionForm,
    InteractionWrapper,
    EmojisAnimation,
    SlideNavigation,
    PictureSticker,
    TextSticker,
    CallToAction,
  },

  data() {
    return {
      errorIndex: 0,
      contentIsLoaded: false,
      previewImage: null,

      // NOTE: needs only for analytics' mixin
      categoryPostfix: "SR",

      interactions: undefined,
      isShowEmojiAnimation: false,
      clickedEmojiIconUrl: undefined,
      isEmojiQuerySuccess: false,
      isPollQuerySuccess: false,
      isQuestionQuerySuccess: false,

      countOfActiveQueries: 0,

      stickers: undefined,

      callToActions: undefined,
    };
  },

  created() {
    if (this.storyMedia.interactions) {
      this.interactions = this.storyMedia.interactions;
    }

    if (this.storyMedia.stickers) {
      this.stickers = this.storyMedia.stickers;
    }

    if (this.storyMedia.callToActions) {
      this.callToActions = this.storyMedia.callToActions;
    }
  },

  props: [
    "slide",
    "isSelectedSlide",
    "isShowNavigation",
    "isShowNavigationPrev",
    "isShowNavigationNext",
  ],

  emits: [
    "pauseSlide",
    "playSlide",
    "nextSlide",
    "prevSlide",
    "navigationTouchPause",
    "getCountOfActiveQueries",
    "getVideoDuration",
  ],

  inject: ["preview", "resumeStorie", "settings"],

  computed: {
    storyImagePositionStyles() {
      if (!this.storyMedia || !this.storyMedia.view) {
        return {};
      }

      const { story } = this.storyMedia.view;

      if (!story) {
        return {};
      }

      const styles = getImagePositionStyles(story);
      return styles;
    },

    backgroundImageStyles() {
      if (this.imageSource?.imageHighRes?.length > 0) {
        const imageStyles = {
          "background-image": this.storyMedia.videoUrl
            ? "none"
            : this.previewImage &&
              !this.storyMedia.videoUrl &&
              `url(${this.previewImage})`,
        };

        return imageStyles;
      } else if (this.storyMedia.background?.length > 0) {
        return {
          background: this.storyMedia.background,
        };
      } else {
        return new Error("preview image or background doesn't exist");
      }
    },

    storyMedia() {
      const { slide } = this;

      if (!slide) return {};

      return slide.media[0];
    },

    imageSource() {
      const { storyMedia } = this;

      return this.imageURL(storyMedia, true);
    },
  },

  methods: {
    sendClickLinkAnalytic(link, linkTitle) {
      const story = this.slide;
      this.sendAnalyticEvent("ClickLink", {
        provPostId:
          story.instagramPost?.frisbuyId ?? story.providerPost.frisbuyId,
        fbProduct: story.frisbuyPost.id,
        igCode: story.providerPost
          ? story.providerPost.shortcode
          : story.instagramPost.shortcode,
        // link: good.id,
        linklabel: linkTitle,
        // offer: good.sku.offerId,
        linkUrl: link,
      });
    },

    async onClickInteractionHandler(interaction) {
      const CODE = getAnonimousUserCode();
      const API_BODY = {
        code: CODE,
        loggedInId: this.settings.logged_in_id || null,
        email: this.settings.logged_in_email || null,
        phone: this.settings.logged_in_phone || null,
        username: this.settings.logged_in_name || null,
        embedId: this.settings.embedId,
        frisbuyPostId: this.slide.frisbuyPost.id,
        carouselPosition: null,

        questionAnswer:
          interaction.type === "question" ? interaction.answer : null,
        emojiId: interaction.type === "emoji" ? interaction.id : null,
        pollVote: interaction.type === "poll" ? interaction.answer.id : null,
      };

      this.countOfActiveQueries += 1;
      const isQuerySuccess = await sendInteraction(API_BODY);
      this.countOfActiveQueries -= 1;

      if (!isQuerySuccess) {
        return;
      }

      if (interaction.type === "question") {
        this.isQuestionQuerySuccess = true;
      }

      if (interaction.type === "emoji") {
        this.interactions[interaction.index].emojis[
          interaction.emojiIndex
        ].isSelected = true;

        this.isEmojiQuerySuccess = true;
        this.showEmojiAnimation(interaction.url);
      }

      if (interaction.type === "poll") {
        this.isPollQuerySuccess = true;

        const updatedPolls = this.interactions[interaction.index].polls.map(
          (poll) => {
            let pollCopy = JSON.parse(JSON.stringify(poll));

            if (poll.id === interaction.answer.id) {
              pollCopy.isSelected = true;
              pollCopy.count += 1;
            }

            return pollCopy;
          }
        );

        this.interactions[interaction.index].polls = updatedPolls;
      }
    },

    showEmojiAnimation(emojiIcon) {
      this.clickedEmojiIconUrl = emojiIcon;

      this.$nextTick(() => {
        this.isShowEmojiAnimation = true;
      });
    },

    questionFormSelectInputHandler(isSelected) {
      this.isQuestionQuerySuccess = false;

      if (isSelected) {
        this.$emit("pauseSlide");
      } else {
        this.$emit("playSlide");
      }
    },

    getInteractionPositionStyles(coords) {
      const styles = getImagePositionStyles(coords);
      return styles;
    },

    errorImageHandler(e) {
      if (this.storyMedia.videoUrl) return;

      if (this.backgroundImageStyles["background"]) {
        this.contentIsLoaded = true;

        return;
      }

      errorImageHandler(
        { e, errorIndex: this.errorIndex, maxCountOfTryingForSource: 2 },
        (recoveryURL) => {
          this.$refs.image.src = recoveryURL;
          this.errorIndex += 1;
          this.load();
        },
        () => {
          setTimeout(() => {
            if (this.isSelectedSlide) {
              this.$emit("nextSlide");
            }
          }, 1000);

          this.errorIndex = 0;
          return;
        }
      );
    },

    errorVideoHandler(e) {
      errorVideoHandler(
        { e, errorIndex: this.errorIndex, maxCountOfTryingForSource: 2 },
        (recoveryURL) => {
          this.$refs.video.src = recoveryURL;
          this.errorIndex += 1;
          this.$refs.video.load();
        },
        () => {
          setTimeout(() => {
            if (this.isSelectedSlide) {
              this.$emit("nextSlide");
            }
          }, 1000);

          this.errorIndex = 0;
          return;
        }
      );
    },

    async load() {
      return new Promise((res) => {
        const { contentIsLoaded, $refs } = this;
        const { image, video } = $refs;

        if (contentIsLoaded) return;

        if (!image?.src && this.storyMedia.background?.length > 0) {
          this.contentIsLoaded = true;
          return;
        }

        image.onload = () => {
          this.previewImage = image.src;

          if (!video) {
            this.contentIsLoaded = true;
            res(true);
          }
        };

        image.src = image.dataset.src;

        if (video) {
          video.src = video.dataset.src;
          video.load();

          video.onloadeddata = () => {
            this.contentIsLoaded = true;
            res(true);
          };
        }
      });
    },

    async play(setTransition) {
      const { $refs, preview } = this;
      const { video } = $refs;

      if (!this.contentIsLoaded) {
        await this.load();
      }

      if (video) {
        this.$emit("getVideoDuration", video.duration);

        const playPromise = video.play();

        playPromise.then(() => {
          const waitingVideoProgress = setInterval(() => {
            if (video.currentTime > 0) {
              clearInterval(waitingVideoProgress);
              this.preview.checkAudio(video);
            }
          }, 20);

          if (setTransition) {
            setTransition(video.duration);
          }
        });

        playPromise.catch((error) => {
          // console.error(error);
          // [WARNING] THIS HANDLER BUGGED
          /*
            Uncaught (in promise) DOMException:
            The play() request was interrupted by a call to pause().
          */

          if (error.code === 9) {
            preview.switchToNext();
          }

          // if (error.code === 9) {
          //   preview.switchToNext();
          // } else {
          //   video.muted = true;
          //   console.log("playPromise.catch play");
          /*
            [WARNING]
            Page may FREEZE when switching slides QUICKLY
          */
          //   this.play();
          // }
        });
      } else if (setTransition) {
        setTransition(5);
      }
    },

    stop() {
      const { video } = this.$refs;

      if (!video) return;

      video.pause();
      video.currentTime = 0;
    },

    pause() {
      const { video } = this.$refs;

      if (!video) return;

      video.pause();
    },
  },

  watch: {
    slide: {
      handler() {
        this.isShowEmojiAnimation = false;
        this.errorIndex = 0;
      },

      immediate: true,
    },

    isSelectedSlide: {
      handler(value) {
        if (value && !this.contentIsLoaded) {
          this.load();
        }
      },
      immediate: true,
    },

    countOfActiveQueries(count) {
      this.$emit("getCountOfActiveQueries", count);
    },
  },
};
</script>

<style lang="scss">
@keyframes slide-switch {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.frisbuy-blur-background {
  filter: blur(10px);
}
.slide-switch-enter-active {
  animation: slide-switch 0.5s;
  z-index: 2;
}

.frisbuy-story-slide__wrapper {
  position: absolute;
  inset: 0;

  width: unset;
  height: unset;
  max-width: 100%;
  max-height: 100%;
  aspect-ratio: 9 / 16;

  border-radius: 10px;

  background-color: rgba(0, 0, 0, 0.5);

  margin: auto;

  overflow: hidden;

  z-index: 0;
}

.frisbuy-story-slide {
  & > img {
    display: block;
    position: relative;
    max-width: 100%;
    max-height: 100%;

    visibility: hidden;
  }
}

.frisbuy-story-slide--current {
  position: absolute;

  top: 0;
  left: 0;

  width: 100%;
  height: 100%;

  overflow: visible;
  z-index: 2;
}

.frisbuy-emoji-animation {
  z-index: 3;
}

.frisbuy-interaction-wrapper {
  z-index: 1;
  .frisbuy-interaction-frame {
    z-index: 2;
  }
}
</style>
